/**
 * @module IDEE/filter/spatial
 */
import * as jsts from 'jsts';
import Spatial from './Spatial';
import WKT from '../format/WKT';
import { isArray, isObject } from '../util/Utils';
import Vector from '../layer/Vector';
import Feature from '../feature/Feature';
import { getValue } from '../i18n/language';

/**
  * Transforma parámetros a geometrías.
  *
  * @function
  * @param {IDEE.layer.Vector|IDEE.Feature|object|Array<IDEE.Feature|object>} paramParameter
  * Capa o geometría sobre la que se realiza la consulta.
  * @return {Array} Geometría.
  * @api
  */
export const parseParamToGeometries = (paramParameter) => {
  let param = paramParameter;
  let geometries = [];
  if (param instanceof Vector) {
    geometries = [...param.getFeatures().map((feature) => feature.getGeometry())];
  } else {
    if (!isArray(param)) {
      param = [param];
    }
    geometries = param.map((p) => {
      let geom;
      if (p instanceof Feature) {
        geom = p.getGeometry();
      } else if (isObject(p)) {
        geom = p;
      }
      return geom;
    });
  }

  return geometries;
};

/**
  * Transforma operación y geometrías a filtro CQL.
  *
  * @private
  * @function
  * @param {Array} geometries Geometría.
  * @param {String} operation Operación.
  * @return {String} Filtro.
  */
const toCQLFilter = (operation, geometries) => {
  let cqlFilter = '';
  try {
    const wktFormat = new WKT();
    geometries.forEach((value, index) => {
      if (index !== 0) {
        // es un OR porque se hace una interseccion completa con todas
        // las geometries
        cqlFilter += ' OR ';
      }
      const geometry = value;
      if (geometry.type.toLowerCase() === 'point') {
        geometry.coordinates.length = 2;
      }
      const formatedGeometry = wktFormat.writeFeature(geometry);
      cqlFilter += `${operation}({{geometryName}}, ${formatedGeometry})`;
    });
  } catch (error) {
    if (error.includes(getValue('exception').wkt_method)) {
      // eslint-disable-next-line no-console
      console.warn(getValue('exception').wkt_toCQL_method);
    } else {
      throw error;
    }
  }
  return cqlFilter;
};

/**
  * Esta función crea un filtro espacial para saber qué entidades contienen otra entidad o capa.
  *
  * @function
  * @param {IDEE.layer.Vector|object} param Capa o geometría sobre la que se realiza la consulta.
  * @return {Spatial} Filtro.
  * @api
  */
export const CONTAIN = (param) => {
  const geometries = parseParamToGeometries(param);
  return new Spatial((geometryToFilter, index) => {
    const geojsonParser = new jsts.io.GeoJSONReader();
    const jtsGeomToFilter = geojsonParser.read(geometryToFilter);
    return geometries.some((geom) => {
      const jtsGeom = geojsonParser.read(geom);
      return jsts.operation.relate.RelateOp.contains(jtsGeom, jtsGeomToFilter);
    });
  }, {
    cqlFilter: toCQLFilter('CONTAINS', geometries),
  });
};

/**
  * Esta función crea un filtro espacial para saber qué objetos geográficos
  * separan otros objetos geográficos o capa.
  *
  * @function
  * @param {IDEE.layer.Vector|object} param Capa o geometría sobre la que se realiza la consulta.
  * @return {Spatial} Filtro.
  * @api
  */
export const DISJOINT = (param) => {
  const geometries = parseParamToGeometries(param);
  return new Spatial((geometryToFilter, index) => {
    const geojsonParser = new jsts.io.GeoJSONReader();
    const jtsGeomToFilter = geojsonParser.read(geometryToFilter);
    return geometries.some((geom) => {
      const jtsGeom = geojsonParser.read(geom);
      return !(jsts.operation.relate.RelateOp.intersects(jtsGeomToFilter, jtsGeom));
    });
  }, {
    cqlFilter: toCQLFilter('DISJOINT', geometries),
  });
};

/**
  * Esta función crea un filtro espacial para saber qué objetos
  * geográficos dentro de otros objetos geográficos o capa.
  *
  * @function
  * @param {IDEE.layer.Vector|object} param Capa o geometría sobre la que se realiza la consulta.
  * @return {Spatial} Filtro.
  * @api
  */
export const WITHIN = (param) => {
  const geometries = parseParamToGeometries(param);
  return new Spatial((geometryToFilter, index) => {
    const geojsonParser = new jsts.io.GeoJSONReader();
    const jtsGeomToFilter = geojsonParser.read(geometryToFilter);
    return geometries.some((geom) => {
      const jtsGeom = geojsonParser.read(geom);
      return jsts.operation.relate.RelateOp.contains(jtsGeom, jtsGeomToFilter);
    });
  }, {
    cqlFilter: toCQLFilter('WITHIN', geometries),
  });
};

/**
  * Esta función crea un filtro espacial para saber qué objeto geográfico
  * se cruza con otra entidad o capa.
  *
  * @function
  * @param {IDEE.layer.Vector|IDEE.Feature|object|Array<IDEE.Feature|object>} param
  * Capa o geometría sobre la que se realiza la consulta.
  * @return {Spatial} Filtro.
  * @api
  */
export const INTERSECT = (param) => {
  const geometries = parseParamToGeometries(param);
  return new Spatial((geometryToFilter, index) => {
    const geojsonParser = new jsts.io.GeoJSONReader();
    const jtsGeomToFilter = geojsonParser.read(geometryToFilter);
    return geometries.some((geom) => {
      const jtsGeom = geojsonParser.read(geom);
      return jsts.operation.relate.RelateOp.intersects(jtsGeomToFilter, jtsGeom);
    });
  }, {
    cqlFilter: toCQLFilter('INTERSECTS', geometries),
  });
};

/**
 * Este comentario no se verá, es necesario incluir
 * una exportación por defecto para que el compilador
 * muestre las funciones.
 *
 * Esto se produce por al archivo normaliza-exports.js
 * @api stable
 */
export default {};
